<template>
  <Swiper
    :space-between="30"
    :free-mode="true"
    :modules="[FreeMode]"
    :breakpoints="breakpoints"
    class="h-full w-full"
  >
    <SwiperSlide class="h-full">
      <Kpi
        :value="formattedMetrics?.plant_utilisation"
        :label="$t('ProjectSelector.KpiSwiper.plantUtilisation')"
        :threshold="plantUtilisationThresholds(kpiMetrics?.plant_utilisation)"
        :title="plantUtilisationTitle(kpiMetrics?.plant_utilisation)"
      />
    </SwiperSlide>

    <SwiperSlide class="h-auto">
      <Kpi
        :value="formattedMetrics?.plant_utilisation_week"
        :label="$t('ProjectSelector.KpiSwiper.plantUtilisation')"
        :subtitle="`(${$t('ProjectSelector.KpiSwiper.weekCommencing')} ${date.toFormat('dd/MMM')})`"
        :threshold="plantUtilisationThresholds(kpiMetrics?.plant_utilisation_week)"
        :title="plantUtilisationTitle(kpiMetrics?.plant_utilisation_week)"
      />
    </SwiperSlide>

    <SwiperSlide>
      <Kpi
        :threshold="(kpiMetrics?.labour_hours > 0) ? 'high' : 'low'"
        :value="formattedMetrics?.labour_hours"
        :label="$t('ProjectSelector.KpiSwiper.labourHours')"
      />
    </SwiperSlide>

    <SwiperSlide>
      <Kpi
        :threshold="(kpiMetrics?.plant_hours > 0) ? 'high' : 'low'"
        :value="formattedMetrics?.plant_hours"
        :label="$t('ProjectSelector.KpiSwiper.plantHours')"
      />
    </SwiperSlide>

    <SwiperSlide>
      <Kpi
        :threshold="varianceThresholds(kpiMetrics?.total_variance)"
        :value="formattedMetrics?.total_variance"
        :label="$t('ProjectSelector.KpiSwiper.performance')"
        :title="varianceTitle(kpiMetrics?.total_variance)"
      />
    </SwiperSlide>

    <SwiperSlide>
      <Kpi
        :threshold="varianceThresholds(kpiMetrics?.variance_week)"
        :value="formattedMetrics?.variance_week"
        :label="$t('ProjectSelector.KpiSwiper.performance')"
        :subtitle="`(${$t('ProjectSelector.KpiSwiper.weekCommencing')} ${date.toFormat('dd/MMM')})`"
        :title="varianceTitle(kpiMetrics?.variance_week)"
      />
    </SwiperSlide>
  </Swiper>
</template>

<script setup lang="ts">
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
// import required modules
import Kpi from '@/components/ProjectSelector/Kpi.vue';
import { australianCurrency, australianNumber } from '@/helpers';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { DateTime } from 'luxon';
import { FreeMode } from 'swiper/modules';
import { computed } from 'vue';

type metrics = {
  plant_utilisation_week: number;
  plant_utilisation: number;
  labour_hours: number;
  plant_hours: number;
  total_variance: number;
  variance_week: number;
};

const props = defineProps<{
  kpiMetrics?: metrics;
  date: DateTime;
}>();

const formattedMetrics = computed(() => {
  if(!props.kpiMetrics) {
    return {};
  }

  return {
    plant_utilisation_week: `${australianNumber(props.kpiMetrics.plant_utilisation_week)}%`,
    plant_utilisation: `${australianNumber(props.kpiMetrics.plant_utilisation)}%`,
    labour_hours: `${australianNumber(props.kpiMetrics.labour_hours)}`,
    plant_hours: `${australianNumber(props.kpiMetrics.plant_hours)}`,
    total_variance: `${australianCurrency(props.kpiMetrics.total_variance)}`,
    variance_week: `${australianCurrency(props.kpiMetrics.variance_week)}`,
  };
});

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

const totalContractValue = computed(() => {
  return wbsCodesStore.models.where('project_id', projectsStore.project?.id).get().reduce((total, wbsCode) => {
    return wbsCode.tender_cost;
  }, 0) || 0;
});

const plantUtilisationThresholds = (utilisation: number | undefined) => {
  if(utilisation === undefined) {
    return 'low';
  }

  if(utilisation < 60) {
    return 'low';
  } else if(utilisation < 80) {
    return 'medium';
  }

  return 'high';
};

const plantUtilisationTitle = (utilisation: number | undefined) => {
  if(utilisation === undefined) {
    return t('ProjectSelector.KpiSwiper.thresholds.redUnknown');
  }

  if(utilisation < 60) {
    return t('ProjectSelector.KpiSwiper.thresholds.redUtilisationLow');
  } else if(utilisation < 80) {
    return t('ProjectSelector.KpiSwiper.thresholds.amberUtilisationMedium');
  }

  return t('ProjectSelector.KpiSwiper.thresholds.greenUtilisationHigh');
};

const varianceThresholds = (performance: number | undefined) => {
  if(performance === undefined) {
    return 'low';
  }

  if(performance > totalContractValue.value * 0.05) {
    return 'high';
  }

  if(performance < totalContractValue.value * 0.05 && performance > totalContractValue.value * -0.05) {
    return 'medium';
  }

  return 'low';
};

const varianceTitle = (performance: number | undefined) => {
  if(performance === undefined) {
    return t('ProjectSelector.KpiSwiper.thresholds.redUnknown');
  }

  if(performance > totalContractValue.value * 0.05) {
    return t('ProjectSelector.KpiSwiper.thresholds.greenPerformanceHigh');
  }

  if(performance < totalContractValue.value * 0.05 && performance > totalContractValue.value * -0.05) {
    return t('ProjectSelector.KpiSwiper.thresholds.amberPerformanceMedium');
  }

  return t('ProjectSelector.KpiSwiper.thresholds.redPerformanceLow');
};

const breakpoints = {
  '1700': {
    slidesPerView: 6,
  },

  '1536': {
    slidesPerView: 4.5,
  },

  '1280': {
    slidesPerView: 4.5,
  },

  '1024': {
    slidesPerView: 4.5,
  },

  '768': {
    slidesPerView: 3.5,
  },
};
</script>

<style scoped></style>
